import React, { useCallback, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '../components/icons';
import { CarteScore } from '../components/CarteScore';
import { LigneAction } from '../components/LigneAction';
import { ScreenHeader } from '../components/ScreenHeader';
import { t } from '../i18n';
import { useCvOverview } from '../hooks/useCvOverview';
import { useGabarit } from '../hooks/useGabarit';
import { pickDocumentFile, type PickedFile } from '../services/mediaPicker';
import { colors, formes, layout, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { styleMiroir, TEXTE_LTR } from '../i18n/sens';

export interface MyCvScreenProps {
  onBack?: () => void;
  // « Note mon CV » : notation sans fichier prérempli.
  onOpenScore: () => void;
  // Le fichier est choisi ICI, pas dans le navigateur : un format refusé ou trop lourd doit
  // s'afficher là où la personne a tapé, pas se perdre en route.
  onImportCv: (file: PickedFile) => void;
  onOpenCreate: () => void;
  onOpenImprove: (file: PickedFile) => void;
  onOpenList: () => void;
}

// Message d'état de la carte de score : ce que la personne doit comprendre en un coup d'œil.
export function scoreMessage(score: number | null, adviceCount: number): string {
  if (score === null) return t('myCv.scoreNone');
  if (adviceCount === 0) return t('myCv.scoreClean');
  // Pluriel délégué à i18n-js : l'accord se joue sur `count`, et chaque langue a ses propres
  // formes — l'anglais n'accorde pas le verbe comme le français.
  return t('myCv.scoreAdvice', { count: adviceCount });
}

// Trois feuilles en éventail : « plusieurs CV », sans chiffre ni contenu à lire.
function PileCv() {
  return (
    <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={styles.pile}>
      {[-8, 0, 8].map((angle, i) => (
        <View key={angle} style={[styles.feuille, { left: i * 10, transform: [{ rotate: `${angle}deg` }] }]} />
      ))}
    </View>
  );
}

/**
 * Entité « Mon CV » : score ATS, premier conseil, notation et optimisation, CV enregistrés.
 *
 * Poussé depuis le sommaire Carrière. Purement une FAÇADE de navigation : chaque action pousse
 * l'écran existant, aucun parcours n'est fusionné ni réécrit. L'écran ne porte aucun état non
 * enregistré, il n'a donc pas besoin de garde de sortie.
 *
 * Le bandeau « CV noté : titre / Changer » est l'enfant de la `CarteScore`. Il n'apparaît que si le
 * serveur a conservé un titre (notation faite depuis le lot 9b) : une notation plus ancienne, ou
 * faite sur un texte sans nom de fichier, n'en a pas, et le bandeau reste masqué plutôt que vide.
 */
export function MyCvScreen({
  onBack,
  onOpenScore,
  onImportCv,
  onOpenCreate,
  onOpenImprove,
  onOpenList,
}: MyCvScreenProps) {
  const { lastScore, cvCount, refresh } = useCvOverview();
  const { large } = useGabarit();
  // L'écran reste monté sous l'éditeur : au retour, un CV tout juste enregistré doit se compter. Le
  // premier focus est sauté, le montage vient de lire.
  const premierFocus = useRef(true);
  useFocusEffect(
    useCallback(() => {
      if (premierFocus.current) {
        premierFocus.current = false;
        return;
      }
      refresh();
    }, [refresh]),
  );
  const [pickError, setPickError] = useState<string | null>(null);
  const score = lastScore?.ats_score ?? null;
  const adviceCount = lastScore?.advice.length ?? 0;
  // Le premier conseil de la dernière notation : déjà persisté, donc lu sans relancer d'analyse.
  const premierConseil = lastScore?.advice[0]?.trim() || null;
  const titreCv = lastScore?.cv_title?.trim() || null;

  // Sélection d'un CV puis remise à l'appelant. Annulation = rien, silencieusement.
  const pickThen = async (then: (file: PickedFile) => void) => {
    setPickError(null);
    const outcome = await pickDocumentFile();
    if (outcome.canceled) return;
    if (outcome.error) return setPickError(outcome.error);
    if (outcome.file) then(outcome.file);
  };

  const sScore = (
    <>
        {/* Carte de score : 0/100 tant qu'aucune notation n'a eu lieu, jamais un état vide muet.
            Le sommaire, lui, ne montre PAS ce 0 : décourageant à l'entrée de l'onglet. */}
        <CarteScore
          testID="mycv-score-card"
          anneauTestID="mycv-score-ring"
          detailTestID="mycv-score-message"
          score={score ?? 0}
          titre={t('myCv.scoreLabel')}
          detail={scoreMessage(score, adviceCount)}
          action={
            <Pressable
              accessibilityRole="button"
              onPress={onOpenScore}
              testID="mycv-score-action"
              hitSlop={{ top: 5, bottom: 5 }}
              style={styles.scoreAction}
            >
              <Text style={styles.scoreActionText}>
                {score === null ? t('myCv.scoreActionFirst') : t('myCv.scoreActionDetail')}
              </Text>
              <View style={styleMiroir()}>
                <Ionicons name="arrow-forward" size={15} color={colors.night} />
              </View>
            </Pressable>
          }
        >
          {titreCv ? (
            <View testID="mycv-cv-note" style={styles.cvNote}>
              <View style={styles.cvNoteTextes}>
                <Text style={styles.cvNoteLabel}>{t('myCv.cvNote')}</Text>
                <Text testID="mycv-cv-note-titre" numberOfLines={1} style={[styles.cvNoteTitre, TEXTE_LTR]}>
                  {titreCv}
                </Text>
              </View>
              <Pressable
                testID="mycv-cv-note-changer"
                accessibilityRole="button"
                accessibilityLabel={`${t('myCv.changer')}, ${t('myCv.cvNote')}`}
                onPress={onOpenScore}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                style={styles.cvNoteChanger}
              >
                <Text style={styles.cvNoteChangerTexte}>{t('myCv.changer')}</Text>
              </Pressable>
            </View>
          ) : null}
        </CarteScore>

            </>
  );
  const sPriorite = (
    <>
{premierConseil ? (
          <Pressable
            testID="mycv-priorite"
            accessibilityRole="button"
            accessibilityLabel={`${t('myCv.priorityLabel')}, ${premierConseil}`}
            onPress={onOpenScore}
            style={styles.priorite}
          >
            <View style={styles.prioriteIcone}>
              <MaterialCommunityIcons name="magnify" size={20} color={colors.electricDeep} />
            </View>
            <View style={styles.prioriteTextes}>
              <Text style={styles.prioriteLabel}>{t('myCv.priorityLabel')}</Text>
              <Text style={styles.prioriteConseil} numberOfLines={2}>
                {premierConseil}
              </Text>
            </View>
            <View style={styleMiroir()}>
              <Ionicons name="chevron-forward" size={18} color={colors.grey400} />
            </View>
          </Pressable>
        ) : null}

            </>
  );
  const sTuiles = (
    <>
<View style={styles.tuiles}>
          <Pressable
            testID="mycv-score"
            accessibilityRole="button"
            accessibilityLabel={`${t('myCv.scoreRowTitle')}, ${t('myCv.scoreRowSubtitle')}`}
            onPress={onOpenScore}
            style={[styles.tuile, styles.tuileClaire, large && styles.tuileLarge]}
          >
            <View style={[styles.tuileIcone, { backgroundColor: colors.blueSoft }]}>
              <MaterialCommunityIcons name="magnify" size={20} color={colors.electricDeep} />
            </View>
            <View>
              <Text style={styles.tuileTitre}>{t('myCv.scoreRowTitle')}</Text>
              <Text style={styles.tuileSousTitre}>{t('myCv.scoreRowSubtitle')}</Text>
            </View>
          </Pressable>
          <Pressable
            testID="mycv-improve"
            accessibilityRole="button"
            accessibilityLabel={`${t('myCv.optimiseTitle')}, ${t('myCv.optimiseSubtitle')}`}
            onPress={() => void pickThen(onOpenImprove)}
            style={[styles.tuile, large && styles.tuileLarge]}
          >
            {/* Plus sombre que la maquette (#578DEA) : le blanc n'y tenait que 3,3:1. */}
            <LinearGradient
              colors={[colors.electricDeep, colors.violetLogo]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            <View style={[styles.tuileIcone, { backgroundColor: 'rgba(255, 255, 255, 0.2)' }]}>
              <MaterialCommunityIcons name="auto-fix" size={20} color={colors.white} />
            </View>
            <View>
              <Text style={[styles.tuileTitre, { color: colors.white }]}>{t('myCv.optimiseTitle')}</Text>
              <Text style={[styles.tuileSousTitre, { color: colors.white }]}>{t('myCv.optimiseSubtitle')}</Text>
            </View>
          </Pressable>
        </View>

            </>
  );
  const sErreur = (
    <>
{pickError ? (
          <Text testID="mycv-pick-error" style={styles.error}>
            {pickError}
          </Text>
        ) : null}

            </>
  );
  const sMesCv = (
    <>
<Text style={styles.sectionTitle}>{t('myCv.myCvs')}</Text>
        <LigneAction
          testID="mycv-list"
          icone={<PileCv />}
          titre={cvCount === null ? t('myCv.savedListOpen') : t('myCv.savedCount', { count: cvCount })}
          sousTitre={t('myCv.listSubtitle')}
          onPress={onOpenList}
        />
        <View style={styles.pastilles}>
          <Pressable
            testID="mycv-import"
            accessibilityRole="button"
            onPress={() => void pickThen(onImportCv)}
            style={styles.pastille}
          >
            <MaterialCommunityIcons name="tray-arrow-up" size={18} color={colors.electricInk} />
            <Text style={styles.pastilleTexte}>{t('myCv.importShort')}</Text>
          </Pressable>
          <Pressable testID="mycv-create" accessibilityRole="button" onPress={onOpenCreate} style={styles.pastille}>
            <MaterialCommunityIcons name="file-plus-outline" size={18} color={colors.electricInk} />
            <Text style={styles.pastilleTexte}>{t('myCv.createShort')}</Text>
          </Pressable>
        </View>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('myCv.title')} variante="carte" testID="mycv-back" />

      <ScrollView
        contentContainerStyle={[styles.content, large && styles.contentLarge]}
        showsVerticalScrollIndicator={false}
      >
        {/* Ordinateur et tablette : deux colonnes. À gauche l'état du CV (score, priorité), à droite les
            actions (noter, optimiser, retrouver ses CV). Le téléphone garde la pile verticale. */}
        {large ? (
          <View testID="mycv-colonnes" style={styles.colonnes}>
            <View style={styles.colonne}>
              {sScore}
              {sPriorite}
            </View>
            <View style={styles.colonne}>
              {sTuiles}
              {sErreur}
              {sMesCv}
            </View>
          </View>
        ) : (
          <>
            {sScore}
            {sPriorite}
            {sTuiles}
            {sErreur}
            {sMesCv}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const CARD_RADIUS = radius.xl;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.md, paddingBottom: spacing.xxl },
  contentLarge: {
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
    gap: spacing.xl,
  },
  colonnes: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xl },
  colonne: { flex: 1, minWidth: 0, gap: spacing.lg },
  tuileLarge: { minHeight: spacing.xxl * 5, padding: spacing.xl, justifyContent: 'space-between' },

  sectionTitle: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    marginTop: spacing.sm,
    textTransform: 'uppercase',
  },

  // ── Carte de score ──
  scoreAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minHeight: formes.hauteur.sm - 6,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
  },
  scoreActionText: { fontSize: typography.small.size, fontWeight: '800', color: colors.night },

  // ── Bandeau « CV noté » (dans la carte nuit) ──
  cvNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.14)',
  },
  cvNoteTextes: { flex: 1, minWidth: 0, gap: 1 },
  cvNoteLabel: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    letterSpacing: typography.caption.letterSpacing,
    textTransform: 'uppercase',
    color: colors.blancAtenue,
  },
  cvNoteTitre: { fontSize: typography.small.size, fontWeight: '800', color: colors.white },
  cvNoteChanger: { minHeight: formes.cible, justifyContent: 'center' },
  cvNoteChangerTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.cyan },

  // ── « À corriger en premier » ──
  priorite: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: CARD_RADIUS,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  prioriteIcone: {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.blueSoft,
  },
  prioriteTextes: { flex: 1, minWidth: 0, gap: 2 },
  prioriteLabel: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    textTransform: 'uppercase',
    color: colors.electricInk,
  },
  prioriteConseil: { fontSize: typography.bodyMd.size, lineHeight: typography.bodyMd.lineHeight, fontWeight: '800', color: colors.night },

  // ── Notation et optimisation ──
  tuiles: { flexDirection: 'row', gap: spacing.md },
  tuile: {
    flex: 1,
    minWidth: 0,
    gap: spacing.lg,
    padding: spacing.lg,
    borderRadius: CARD_RADIUS,
    overflow: 'hidden',
  },
  tuileClaire: { backgroundColor: colors.white, ...shadow.card },
  tuileIcone: {
    width: 38,
    height: 38,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tuileTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  tuileSousTitre: { marginTop: 2, fontSize: typography.small.size, color: colors.grey500 },

  // ── Mes CV ──
  pile: { width: 44, height: 36 },
  feuille: {
    position: 'absolute',
    top: 2,
    width: 24,
    height: 32,
    borderRadius: 5,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
  },
  pastilles: { flexDirection: 'row', gap: spacing.md },
  pastille: {
    flex: 1,
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.bordureClaire,
    backgroundColor: colors.white,
  },
  pastilleTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },

  error: { fontSize: typography.small.size, color: colors.error },
});
